import moment from 'moment';
import { get } from '@aws-amplify/api';
import {
  ASR_DEFAULT_MESSAGE,
  L8_MANAGER_DETAILS,
  RISK_REMEDIATOR_API_ERROR,
  RISK_REMEDIATOR_DIRECTS_API_ERROR,
  RISK_REMEDIATOR_MANAGER_API_ERROR,
  RISK_TYPE_FLAG,
  SPLIT_PANEL_CONFIG
} from 'src/components/fintech-ops/risk-remediator/constants';
import { logger } from 'src/logger';
import { Button, KeyValuePairs, Link, ProgressBar, TableProps } from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

export const fetchSamplingDate = (relativeNumberOfDays: number, flag = 'Current') => {
  const timestamp = flag === 'Previous' ? 'T00:00:00Z' : 'T23:59:59Z';

  const sampleDate = moment().subtract(relativeNumberOfDays, 'days');
  const filterDate = sampleDate.format('YYYY-MM-DD') + timestamp;
  return filterDate;
};

export const fetchRiskRemediatorRisks = async (duration: string, manager: string) => {
  return invokeGetAPI({ apiName: 'RISK_REMEDIATOR_API', path: '/fetch_risks', options: { queryParams: { duration, manager } } }, 'risks');
};

export const filterDataByType = (data: string, dataType: string, manager: string) => {
  if (data == undefined) return [];
  const jsonObj = JSON.parse(data);
  return jsonObj[dataType].filter((obj: Record<string, string>) => obj.leadership_rollup.split(',').includes(manager) || obj.owner_alias == manager);
};

export const fetchManagers = async () => {
  const managerlist: Record<string, string>[] = [{ label: L8_MANAGER_DETAILS.name, value: L8_MANAGER_DETAILS.login }];
  const responseData = await invokeGetAPI(
    { apiName: 'RISK_REMEDIATOR_API', path: '/fetch_managers', options: {} },
    'managers',
    RISK_REMEDIATOR_MANAGER_API_ERROR
  );
  responseData.details.split(',').forEach((manager: string) => {
    managerlist.push({ label: manager.split(':')[1], value: manager.split(':')[0] });
  });
  managerlist.sort((a, b) => a.label.localeCompare(b.label));
  return managerlist;
};

export const filterDatabyRiskCategory = (data: any, riskCategory: string) => {
  if (riskCategory == 'MAWS') {
    return data.filter((obj: any) => obj.resource_type.includes('maws'));
  } else {
    return data.filter((obj: any) => !obj.resource_type.includes('maws'));
  }
};
export const filterByClassification = (data: any[], classification: string): any[] => {
  return data.filter((item: any) => (item.risk_classification || 'Violation') === classification);
};

export const fetchDirects = async (manager: string) => {
  return invokeGetAPI(
    {
      apiName: 'RISK_REMEDIATOR_API',
      path: '/fetch_direct_reports',
      options: {
        queryParams: {
          manager: manager
        }
      }
    },
    'reports',
    RISK_REMEDIATOR_DIRECTS_API_ERROR
  );
};

export const getPanelContent = (items: any[], type: TableProps.SelectionType) => {
  if (type !== 'single' || !items.length) {
    return SPLIT_PANEL_CONFIG.EMPTY_PANEL_CONTENT;
  }

  const item = items[0];
  const review_status = item.review_current_status || item.application_certification_status;
  const isReviewInProgress = ['INPROGRESS', 'INITIATED', 'COMPLETE'].includes(review_status);
  const default_msg_key = isReviewInProgress ? 'notApplicable' : 'reviewNotInitiated';
  const pendingTaskCount = item.total_task_count - item.completed_task_count;

  const keyValueItems = [
    { label: 'Application', value: item.asr_application_name },
    { label: 'Current review status', value: review_status },
    { label: 'Review Progress Status', value: <ProgressBar value={item.review_progress_percentage} /> },
    { label: 'Profiling Task Status', value: item.profiling_task_status || ASR_DEFAULT_MESSAGE[default_msg_key] },
    { label: 'Review Start Date', value: item.review_start_date || ASR_DEFAULT_MESSAGE[default_msg_key] },
    { label: 'Review End Date', value: item.review_end_date || ASR_DEFAULT_MESSAGE[default_msg_key] },
    { label: 'Pending Task Count', value: pendingTaskCount || ASR_DEFAULT_MESSAGE.notApplicable },
    { label: 'Review Classification', value: item.review_classification || ASR_DEFAULT_MESSAGE.dataNotClassified },
    { label: 'Drift Status', value: item.drift_status || 'Not Scanned' },
    { label: 'Drift Count', value: item.drift_count || 0 }
  ];

  const driftDetails = (item.drift_details || []).map((drift: any, idx: number) => (
    <div
      key={idx}
      style={{
        marginBottom: '12px',
        padding: '12px',
        background: drift.confidence === 'CONFIRMED' ? '#fdf3f1' : '#fef9e7',
        borderLeft: `4px solid ${drift.confidence === 'CONFIRMED' ? '#d13212' : '#8a6d3b'}`,
        borderRadius: '0 4px 4px 0'
      }}
    >
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>{drift.question}</div>
      <div style={{ fontSize: '12px', color: '#16191f', marginBottom: '4px' }}>
        <strong>Certified Answer:</strong> No &nbsp;|&nbsp;
        <strong>Current State:</strong> Resources detected that contradict this answer
      </div>
      <div style={{ fontSize: '12px', color: '#16191f', background: '#f2f3f3', padding: '8px', borderRadius: '3px', marginBottom: '4px' }}>
        {drift.evidence && drift.evidence !== 'Answered No at certification'
          ? `${drift.evidence}${drift.accounts ? ` — found in accounts: ${drift.accounts}` : ''}`
          : drift.accounts
          ? `New resources found in accounts: ${drift.accounts}`
          : 'Resources detected post-certification that contradict this answer'}
      </div>
      <div style={{ fontSize: '11px', color: '#687078', display: 'flex', gap: '12px' }}>
        <span>{drift.confidence === 'CONFIRMED' ? '🔴 Confirmed' : '🟡 Likely'}</span>
        {drift.urgency && <span>Urgency: {drift.urgency}</span>}
      </div>
    </div>
  ));

  const driftQuestions = (item.drift_details || []).map((d: any) => d.question).join(', ');
  const agentSpaceUrl = `https://agentspaces.amazon.dev/app?mode=CUSTOM&blueprint=AgentSpacesEmptyBlueprintProd&aimPackageName=DaSFintech-ASRCertificationAssistant&customAgentName=das-asr-assistant&spaceName=ASR+Recertification+${encodeURIComponent(
    item.asr_application_name
  )}&initialPrompt=${encodeURIComponent(
    `Initiate ASR recertification for application ${item.application_id}. Drift detected in: ${driftQuestions}. Please guide me through updating the profiling answers and completing required tasks.`
  )}`;

  return {
    header: item.asr_application_name,
    body: (
      <div>
        <KeyValuePairs columns={3} items={keyValueItems} />
        {driftDetails.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ marginBottom: '12px' }}>
              <Link href={agentSpaceUrl} external variant="primary" fontSize="body-m">
                Launch ASR Recertification in AgentSpaces →
              </Link>
            </div>
            <h4 style={{ fontWeight: 700, fontSize: '14px', marginBottom: '12px' }}>Drift Details</h4>
            {driftDetails}
          </div>
        )}
      </div>
    )
  };
};

export const fetchRiskRemediatorAsrData = async (manager: string) => {
  return invokeGetAPI(
    {
      apiName: 'RISK_REMEDIATOR_API',
      path: '/fetch_asr_details',
      options: {
        queryParams: {
          manager: manager
        }
      }
    },
    'asr'
  );
};

export const fetchRiskRemediatorVpcData = async (manager: string) => {
  return invokeGetAPI(
    {
      apiName: 'RISK_REMEDIATOR_API',
      path: '/fetch_vpc_details',
      options: {
        queryParams: {
          manager: manager
        }
      }
    },
    'vpc'
  );
};

export const fetchOpenRisksForAsrApp = (asrAppName: string, openRisk: []) => {
  const violationList: string[] = [];
  const openRiskDetails: Record<string, string>[] = openRisk.filter((obj: Record<string, string>) => obj.resource_arn == `${asrAppName}`);
  openRiskDetails.forEach((risk: Record<string, string>) => {
    violationList.push(risk.issue_detected);
  });
  return violationList.toString();
};

export const getTop5RiskCategories = (selectCategorySummaryData: Array<{ title: string; value: number }>) => {
  // Sort the array in descending order based on the 'value' (risk count)
  const sortedData = [...selectCategorySummaryData].sort((a, b) => b.value - a.value);

  // Slice the array to get only the top 5 items
  const top5 = sortedData.slice(0, 5);

  return top5;
};

export const invokeGetAPI = async (apiProps: Record<string, any>, dataName: string, errorShortDesc: string = RISK_REMEDIATOR_API_ERROR) => {
  try {
    const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
    apiProps['options']['headers'] = { Authorization: authToken };
    const response = await get({ apiName: apiProps.apiName, path: apiProps.path, options: apiProps?.options }).response;
    const responseData = await response.body.text();
    if (!JSON.parse(responseData)?.details) {
      throw new Error('Invalid response format');
    }
    return JSON.parse(responseData);
  } catch (error: any) {
    logger.error(`Failed to fetch ${dataName} details`, error);
    // NOTE: this used to call useFinTechContext().displayNotification which was a bug (hook in non-component). Error is already logged above.
    throw error;
  }
};
